"use client";

/**
 * ChartsMock — animated mock of the askFinz Charts workspace for
 * /apps/charts. A faux dashboard canvas: a field rail on the left, three KPI
 * tiles, a bar chart and a trend line on the right.
 *
 * Animation: bar heights animate up on intersection, a "field being dragged"
 * highlight cycles through the field list, and the KPI deltas tick up.
 * Pure-presentation — no charts.askfinz.ai product UI exists yet (stub page).
 *
 * Theme-aware via the marketing tokens (hsl(var(--foreground/border/accent))).
 */
import { useEffect, useRef, useState } from "react";
import { BarChart3, LineChart, Table2, GripVertical, Hash, Calendar, Type, TrendingUp } from "lucide-react";

const FIELDS = [
  { icon: Hash,     label: "revenue" },
  { icon: Hash,     label: "orders" },
  { icon: Type,     label: "region" },
  { icon: Calendar, label: "month" },
  { icon: Hash,     label: "margin %" },
];

const KPIS = [
  { label: "Revenue",     value: "€1.24M", delta: "+12.4%" },
  { label: "Orders",      value: "8,932",  delta: "+5.1%"  },
  { label: "Avg. margin", value: "31.8%",  delta: "+0.6pt" },
];

// Bar heights (%) for a simple 8-bar chart.
const BARS = [38, 52, 47, 63, 58, 74, 69, 86];

export function ChartsMock() {
  const [barsVisible, setBarsVisible] = useState(false);
  const [activeField, setActiveField] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      setBarsVisible(false);
      setActiveField(null);

      // Bars animate in
      schedule(() => setBarsVisible(true), 200);

      // Cycle through field highlights
      FIELDS.forEach((_, i) => {
        schedule(() => setActiveField(i), 600 + i * 500);
      });
      schedule(() => setActiveField(null), 600 + FIELDS.length * 500 + 400);

      // Loop
      schedule(runCycle, 600 + FIELDS.length * 500 + 2000);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 border-b hairline px-4 h-10">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/65">
            charts.askfinz.ai · Q4 overview
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-md border hairline bg-card px-2 py-1 text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" /> Live
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[170px_1fr]">
          {/* Field rail */}
          <div className="border-b md:border-b-0 md:border-r hairline p-3">
            <p className="px-1 pb-2 text-[10px] font-mono tracking-[0.18em] uppercase text-foreground/65">
              Fields
            </p>
            <div className="space-y-1">
              {FIELDS.map((f, i) => {
                const Icon = f.icon;
                const isActive = activeField === i;
                return (
                  <div
                    key={f.label}
                    className={
                      "flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-all duration-300 " +
                      (isActive
                        ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-foreground/90 shadow-sm"
                        : "hairline bg-card text-foreground/70")
                    }
                  >
                    <GripVertical className="h-3.5 w-3.5 text-foreground/30" />
                    <Icon className="h-3.5 w-3.5 text-foreground/80" />
                    <span className="font-mono">{f.label}</span>
                    {isActive && (
                      <span className="ml-auto text-[9px] font-mono tracking-[0.14em] uppercase text-[hsl(var(--accent))]">
                        dragging
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t hairline text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65 flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3" />
              <span>Drag onto chart</span>
            </div>
          </div>

          {/* Canvas */}
          <div className="p-4 space-y-4">
            {/* KPI tiles */}
            <div className="grid grid-cols-3 gap-3">
              {KPIS.map((k) => (
                <div key={k.label} className="rounded-xl border hairline bg-card px-3 py-3">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-foreground/65">{k.label}</p>
                  <p className="mt-1 text-lg font-semibold tracking-tight">{k.value}</p>
                  <p className="text-[11px] font-mono text-[hsl(var(--accent))]">{k.delta}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Bar chart */}
              <div className="rounded-xl border hairline bg-card p-3">
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                  <BarChart3 className="h-3.5 w-3.5" /> Revenue by month
                </div>
                <div className="flex h-28 items-end gap-1.5">
                  {BARS.map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t-sm transition-all duration-700"
                      style={{
                        height: barsVisible ? `${h}%` : "4%",
                        transitionDelay: `${i * 60}ms`,
                        background: `hsl(var(--accent) / ${0.35 + (i / BARS.length) * 0.5})`,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Trend line (SVG) */}
              <div className="rounded-xl border hairline bg-card p-3">
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                  <LineChart className="h-3.5 w-3.5" /> Margin trend
                </div>
                <svg viewBox="0 0 200 90" className="h-28 w-full" preserveAspectRatio="none">
                  <polygon
                    fill="hsl(var(--accent) / 0.10)"
                    points="0,70 28,58 56,62 84,44 112,48 140,30 168,34 200,16 200,90 0,90"
                  />
                  <polyline
                    fill="none"
                    stroke="hsl(var(--accent))"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points="0,70 28,58 56,62 84,44 112,48 140,30 168,34 200,16"
                    style={{
                      strokeDasharray: 320,
                      strokeDashoffset: barsVisible ? 0 : 320,
                      transition: "stroke-dashoffset 1.4s ease-out",
                    }}
                  />
                </svg>
              </div>
            </div>

            {/* Table strip */}
            <div className="rounded-xl border hairline bg-card px-3 py-2 flex items-center gap-2 text-[11px] font-mono text-foreground/65">
              <Table2 className="h-3.5 w-3.5" />
              <span>8 rows · region × revenue × margin</span>
              <span className="ml-auto rounded border hairline px-1.5 py-0.5 text-[10px] tracking-[0.12em] uppercase">
                Share ↗
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
